import { Box, Flex, HStack, Icon, Text, Tooltip } from '@chakra-ui/react'
import { type AlertsType } from 'api/cms/alerts/useCountryAlerts'
import { InlineLoading } from 'components'
import _, { isEqual } from 'lodash'

import Alert from 'modules/Pandemic/Alerts/Alert'
import { useEffect, useMemo, useState } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { useFilteredAlerts } from 'modules/Pandemic/Alerts/useFilteredAlerts'
import { AlertsFiltersT, defaultAlertsFilters } from 'utils/useAlertsFilters'
import { Pagination } from './Pagination'
import { AlertsFilters } from 'modules/Pandemic/Alerts/AlertsFilters'
import { Information } from '@carbon/icons-react'

const itemsPerPage = 5

const AlertsInformation =
  'Alerts are categorised according to Airfinity’s Outbreak Risk methodology. This is based on pathogen-specific factors, countermeasure availability and a country’s preparedness. This is distinct from Airfinity’s Threat Level, which has been developed specifically for botulism and is available in the reports.'

const SimpleCard = ({ h, children }: any) => {
  return (
    <Box
      h='100%'
      w={'full'}
      border={'1px solid'}
      borderColor='gray3'
      borderRadius='8px'
      overflow={'visible'}
      pl={5}
      pr={1}
      py={4}
    >
      <Flex alignItems={'center'} justifyContent={'space-between'}>
        <Text fontWeight={'semibold'} fontSize={'md'}>
          Latest Alerts
        </Text>
      </Flex>
      <Box
        h={h || 'calc(76vh - 130px)'}
        w='full'
        overflow={'hidden'}
        mt={'10px'}
      >
        <Flex justifyContent='center' alignItems='center' h='80%'>
          {children}
        </Flex>
      </Box>
    </Box>
  )
}

interface DiseaseFilterT {
  label: string
  value: number
  id: number
}

const MpoxDiseaseFilter = {
  label: 'Mpox',
  value: 3292,
  id: 3292,
}
const BotulismDiseaseFilter = {
  label: 'Botulism',
  value: 11976,
  id: 11976,
}

const diseaseFilters: Record<string, DiseaseFilterT[]> = {
  Mpox: [MpoxDiseaseFilter],
  Botulism: [BotulismDiseaseFilter],
}

function getDiseaseFilter(disease: string) {
  return diseaseFilters[disease] || diseaseFilters.Mpox
}

function getDefaultAlertsFilters(disease: string): AlertsFiltersT {
  const filters = {
    ...defaultAlertsFilters,
    diseases: getDiseaseFilter(disease),
  }
  return filters
}
const LatestAlerts = ({
  fullPage = true,
  width,
  pagination = true,
  h,
  disease = 'Mpox',
  columnHeader,
  selectedArea,
}: {
  fullPage?: boolean
  width?: string
  pagination?: boolean
  h?: string
  disease?: string
  columnHeader?: boolean
  selectedArea?: string
}) => {
  const [filters, setFilters] = useState<AlertsFiltersT>(
    getDefaultAlertsFilters(disease)
  )

  useEffect(() => {
    if (selectedArea) {
      const isGlobal = selectedArea === 'global'
      setFilters({
        ...getDefaultAlertsFilters(disease),
        locations: isGlobal
          ? []
          : [
              {
                label: selectedArea,
                id: selectedArea,
                value: selectedArea,
              },
            ],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArea])

  const isBotulism = disease === 'Botulism'

  const [currentPage, setCurrentPage] = useState<number>(1)

  const disableTracking = useMemo(
    () => isEqual(filters, getDefaultAlertsFilters(disease)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters]
  )

  const {
    data,
    canFetchMore,
    isFetchingNextPage,
    fetchMore,
    isFetched,
    isError,
  } = useFilteredAlerts({ maxItems: 100, filters, disableTracking })

  const filteredAlerts = data as any as AlertsType[]

  function filtersCB(_filters: any) {
    setFilters({ ..._filters, ...getDiseaseFilter(disease) })
  }

  const totalPages = Math.ceil(filteredAlerts.length / itemsPerPage)

  const paginatedAlerts = pagination
    ? (() => {
        const startIndex = (currentPage - 1) * itemsPerPage
        return filteredAlerts.slice(startIndex, startIndex + itemsPerPage)
      })()
    : filteredAlerts

  const groupedAlerts = _.groupBy(
    paginatedAlerts,
    (alert) => alert.datePublished
  )

  const sortedDates = Object.keys(groupedAlerts).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  )

  const displayDates = fullPage ? sortedDates : [sortedDates[0]]
  const total = Object.keys(displayDates).length

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  if (isError) {
    const message = isError
      ? 'Sorry, something went wrong loading the alerts'
      : !filteredAlerts?.length && isFetched
        ? 'No alerts at the moment'
        : null

    return (
      <SimpleCard h={h}>
        {message ? (
          <Text
            textAlign={'center'}
            color={isError ? 'error' : 'inherit'}
            fontWeight={'semibold'}
          >
            {message}
          </Text>
        ) : (
          <Flex w='100%' alignItems='center' justifyContent='center'>
            <InlineLoading />
          </Flex>
        )}
      </SimpleCard>
    )
  }

  return (
    <Box
      h='100%'
      w={width || 'full'}
      border={'1px solid'}
      borderColor='gray3'
      borderRadius='8px'
      overflow={'visible'}
      pl={5}
      pr={1}
      py={4}
    >
      <Flex
        alignItems={columnHeader ? 'flex-start' : 'center'}
        justifyContent={'space-between'}
        flexDir={columnHeader ? 'column' : 'row'}
      >
        <HStack>
          <Text minW='max-content' fontWeight={'semibold'} fontSize={'md'}>
            Latest Alerts {isBotulism && '& Outbreak Risk'}
          </Text>
          <Tooltip
            p='0.5rem'
            bg='gray.700'
            color='white'
            label={AlertsInformation}
            aria-label={AlertsInformation}
          >
            <Box fontSize={'12px'}>
              <Icon
                as={Information}
                mb='-4px'
                width='16px'
                height='16px'
                ml='8px'
              />
            </Box>
          </Tooltip>
        </HStack>
        <AlertsFilters
          filtersCB={filtersCB}
          availableFilters={['locations']}
          fixedFilter={getDefaultAlertsFilters(disease)}
        />
      </Flex>
      <Box
        h={h || 'calc(76vh - 130px)'}
        w='full'
        overflow={'hidden'}
        mt={'10px'}
      >
        {filteredAlerts.length === 0 && isFetched && (
          <Flex
            justifyContent='center'
            alignItems='center'
            h='80%'
            fontWeight={'semibold'}
            color='gray.700'
          >
            <Text> No alerts match the selected filters</Text>
          </Flex>
        )}
        {fullPage ? (
          <Virtuoso
            style={{ height: h || 'calc(70vh - 130px)' }}
            totalCount={total}
            itemContent={(index: number) => {
              return (
                <Alert
                  index={index}
                  displayDates={displayDates}
                  groupedAlerts={groupedAlerts}
                  fullPage={false}
                />
              )
            }}
            endReached={() => {
              if (canFetchMore && !isFetchingNextPage) {
                fetchMore()
              }
            }}
          />
        ) : (
          <Box overflowY='auto' h='100%' mt={8}>
            <Alert
              index={0}
              displayDates={displayDates}
              groupedAlerts={groupedAlerts}
              fullPage={false}
            />
            <Box mb='3rem' />
          </Box>
        )}
        {pagination && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            isDisabled={isFetchingNextPage}
          />
        )}
      </Box>
    </Box>
  )
}

export default LatestAlerts
